const NBSP = '\u00A0';

export type MarkdownHeading = {
  text: string;
  id: string;
  level: number;
};

export function preventOrphans(text: string): string {
  return text.replace(/ (\S{1,3}) /g, `${NBSP}$1${NBSP}`);
}

/**
 * Extracts plain text from a hast (HTML AST) node — the raw data structure
 * passed as the `node` prop by react-markdown. Operates on plain JS objects
 * `{type, value, children}`, never on React elements, so it cannot overflow
 * the call stack regardless of tree depth.
 */
export function hastNodeText(node: unknown): string {
  const stack: unknown[] = [node];
  const parts: string[] = [];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === null || typeof current !== 'object') continue;
    const n = current as {
      type?: string;
      value?: string;
      children?: unknown[];
    };
    if (n.type === 'text' && typeof n.value === 'string') {
      parts.push(n.value);
    }
    if (Array.isArray(n.children)) {
      for (let i = n.children.length - 1; i >= 0; i--) {
        stack.push(n.children[i]);
      }
    }
  }
  return parts.join('');
}

/**
 * Converts plain-text bullet notation (\u2022 and " - ") to proper Markdown lists.
 */
export function preprocessListMarkup(content: string): string {
  const hasBullets = content.includes('\u2022');
  if (!hasBullets && !/[.:]\s+-\s+/.test(content)) {
    return content;
  }

  let processed = content
    .replace(/([.:])\s+-\s+/g, '$1\n\n- ')
    .replace(/[ \t]*\u2022[ \t]+/g, '\n- ')
    // Markdown link after sentence end \u2192 new paragraph (skip short abbreviations like "No.", "Ltd.")
    .replace(/(?<=[A-Za-z]{4,})\.\s+(\[)/g, '.\n\n$1');

  if (hasBullets) {
    processed = processed.replace(/ - /g, '\n\n- ');
  }

  return processed.replace(/\n{3,}/g, '\n\n').trim();
}

const isAnchorItem = (line: string) =>
  /^\d+\.\s+\[.+?\]\(#[^)]+\)\s*$/.test(line);
const isOrderedItem = (line: string) => /^\d+\.\s+/.test(line);
const isHeading = (line: string) => /^#{1,6}\s+/.test(line);

function markAnchorBlock(
  lines: string[],
  blockStart: number,
  blockEnd: number,
  toRemove: Set<number>,
): void {
  for (let k = blockStart; k < blockEnd; k++) {
    toRemove.add(k);
  }
  let j = blockStart - 1;
  while (j >= 0 && lines[j].trim() === '') {
    j--;
  }
  if (j >= 0 && isHeading(lines[j])) {
    toRemove.add(j);
  }
}

/**
 * Strips ordered-list blocks where every item is a same-page anchor link
 * (e.g. `1. [Section](#section-id)`). Also strips a heading that immediately
 * precedes such a block (only blank lines between them), since it acts as a
 * TOC title redundant with the product title shown above the description.
 */
export function stripAnchorOnlyList(content: string): string {
  const lines = content.split('\n');
  const toRemove = new Set<number>();

  let i = 0;
  while (i < lines.length) {
    if (!isOrderedItem(lines[i])) {
      i++;
      continue;
    }
    const blockStart = i;
    while (i < lines.length && isOrderedItem(lines[i])) {
      i++;
    }
    if (lines.slice(blockStart, i).every(isAnchorItem)) {
      markAnchorBlock(lines, blockStart, i, toRemove);
    }
  }

  return lines
    .filter((_, idx) => !toRemove.has(idx))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[`*_[\](){}!]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function stripInlineMarkdown(raw: string): string {
  return raw
    .replace(/\\(.)/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1');
}

function extractAnchorId(text: string): string | null {
  const m = text.match(/\{#([^}]+)\}/);
  return m ? m[1] : null;
}

export function extractHeadings(markdown: string): MarkdownHeading[] {
  const headings: MarkdownHeading[] = [];
  const lines = markdown.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // ATX-style: ## Heading {#optional-anchor}
    const atxMatch = line.match(/^(#{1,6})\s+(.+?)(?:\s*\{#[^}]+\})?\s*$/);
    if (atxMatch) {
      const raw = atxMatch[2].trim();
      const text = stripInlineMarkdown(raw);
      const anchor = extractAnchorId(line);
      headings.push({
        text,
        id: anchor ?? slugifyHeading(text),
        level: atxMatch[1].length,
      });
      continue;
    }

    // Setext-style: heading text on one line, underline of = (h1) or - (h2) on next
    const nextLine = lines[i + 1] ?? '';
    const isH1Underline = /^=+\s*$/.test(nextLine);
    const isH2Underline = /^-{2,}\s*$/.test(nextLine);
    if ((isH1Underline || isH2Underline) && line.trim().length > 0) {
      const anchor = extractAnchorId(line);
      const raw = line
        .trim()
        .replace(/\s*\{#[^}]+\}\s*$/, '')
        .trim();
      const text = stripInlineMarkdown(raw);
      headings.push({
        text,
        id: anchor ?? slugifyHeading(text),
        level: isH1Underline ? 1 : 2,
      });
      i++; // skip the underline line
    }
  }

  return headings;
}

export function extractFirstHeading(markdown: string): string {
  const match = markdown.match(/^#{1,6}\s+(.+)$/m);
  return match?.[1]?.trim() ?? 'Section';
}

/** Strips Markdown formatting to produce plain text suitable for JSON-LD or meta tags. */
export function markdownToPlainText(md: string): string {
  return md
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [text](url) → text
    .replace(/\n+/g, ' ')
    .trim();
}
